import { useState, useEffect } from 'react';
import { api } from '../api';
import { getHotelLinks, getRestaurantLinks, getActivityLinks, getFlightLinks, getFlightBookingSteps, getHotelBookingSteps, LinkRow } from '../pilotLinks';

// TripDashboard — the "My Trip" anchor page. Loads the latest Pilot
// plan + multi-city state and renders countdown / today's blocks /
// budget / stops / flights / day-by-day / activities / restaurants /
// watch-outs with booking links + tap-to-call phones inline.
export default function TripDashboard({ trip, onOpenPilot, onOpenPlanFlow }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now] = useState(() => new Date());
  const [memories, setMemories] = useState([]);
  const [allVersions, setAllVersions] = useState([]);

  useEffect(() => {
    if (!trip?.selected_destination_id) { setLoading(false); }
    else {
      api.getPilotChatHistory(trip.selected_destination_id)
        .then(res => { setPlan(res?.plan || null); setLoading(false); })
        .catch(() => setLoading(false));
    }
    // Past trips — every "Apply this plan" creates a trip_memory row
    api.getTripMemory().then(r => setMemories(r?.memories || [])).catch(() => {});
    // Every saved version across all destinations
    api.getTripVersions().then(r => setAllVersions(r?.versions || [])).catch(() => {});
  }, [trip?.selected_destination_id]);

  if (!trip) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center text-brand-ink/70">
        <h1 className="text-2xl font-black mb-3 text-brand-ink">No active trip</h1>
        <p className="text-sm">Pick a destination + dates on the Plan Trip tab, then come back.</p>
      </div>
    );
  }

  const start = trip.date_range_start ? new Date(trip.date_range_start + 'T00:00:00') : null;
  const end = trip.date_range_end ? new Date(trip.date_range_end + 'T23:59:59') : null;
  const daysUntil = start ? Math.ceil((start - now) / 86400000) : null;
  const tripIsActive = start && end && now >= start && now <= end;
  const tripIsPast = end && now > end;
  const currentDay = tripIsActive && start ? Math.ceil((now - start) / 86400000) : null;

  const stops = plan?.per_stop_nights || [];
  const hotels = plan?.hotels || [];
  const activities = plan?.activities || [];
  const restaurants = plan?.restaurants || [];
  const flights = plan?.flight_picks || [];
  const schedule = plan?.daily_schedule || [];
  const todaySchedule = currentDay ? schedule.find(d => d.day_number === currentDay) : null;

  return (
    <div className="max-w-3xl mx-auto pb-20 px-3 sm:px-4">
      <div className="text-center my-6">
        <div className="text-brand-ink/55 text-xs font-bold uppercase tracking-[0.3em]">MY TRIP</div>
        <h1 className="text-2xl sm:text-3xl font-black mt-1 text-brand-ink">{trip.name || 'Your trip'}</h1>
        {start && end && (
          <div className="text-sm text-brand-ink/70 mt-1">
            {start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {' · '}{trip.travelers} traveler{trip.travelers === 1 ? '' : 's'}
          </div>
        )}
        {daysUntil !== null && daysUntil > 0 && (
          <div className="inline-block mt-3 bg-gradient-to-r from-teal to-gold text-navy font-black px-4 py-1.5 rounded-full text-sm">
            {daysUntil} {daysUntil === 1 ? 'day' : 'days'} until takeoff
          </div>
        )}
        {tripIsActive && (
          <div className="inline-block mt-3 bg-rose-500/20 border border-rose-500/40 text-rose-300 font-bold px-4 py-1.5 rounded-full text-sm animate-pulse">
            ✈️ Trip live · Day {currentDay}
          </div>
        )}
        {tripIsPast && (
          <div className="inline-block mt-3 bg-brand-ink/10 text-brand-ink/70 font-bold px-4 py-1.5 rounded-full text-sm">
            Trip complete
          </div>
        )}
      </div>

      {!loading && !plan && (
        <div className="bg-navy-light border-2 border-dashed border-brand-ink/20 rounded-2xl p-6 text-center mb-3">
          <div className="text-base font-bold text-brand-ink mb-2">No Pilot plan yet for this trip</div>
          <div className="text-xs text-brand-ink/70 mb-4">Pilot generates the full A-to-Z itinerary with reasoning, booking links, and tap-to-call phones — but doesn't book anything for you. You execute the bookings on the official sites.</div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button onClick={onOpenPilot} className="bg-gradient-to-r from-teal to-gold text-navy font-bold px-5 py-2.5 rounded-lg">✨ Plan with Pilot</button>
            <button onClick={onOpenPlanFlow} className="bg-brand-ink/10 text-brand-ink font-bold px-5 py-2.5 rounded-lg">Build step-by-step</button>
          </div>
        </div>
      )}

      {/* Past trips show even when no active plan — so user can browse history */}
      {!plan && (memories.length > 0 || allVersions.length > 0) && (
        <PastTripsSection memories={memories} versions={allVersions} />
      )}

      {/* Active trip controls — visible right at top when a plan exists.
          Solves the user's "how do I go back in my trip" — clear CTAs
          to re-open in Pilot OR jump to the manual editor. */}
      {plan && (
        <div className="bg-gradient-to-r from-teal/15 to-gold/10 border-2 border-teal/40 rounded-2xl p-3 mb-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold text-teal uppercase tracking-wider">✓ ACTIVE PLAN LOADED</div>
            <div className="text-sm font-bold text-brand-ink mt-0.5">{trip?.name || 'Your trip'} — scroll for full details</div>
          </div>
          <button onClick={onOpenPilot} className="bg-gradient-to-r from-teal to-gold text-navy font-bold text-xs px-3 py-2 rounded-lg">
            💬 Refine in Pilot
          </button>
          <button onClick={onOpenPlanFlow} className="bg-brand-ink/10 text-brand-ink font-bold text-xs px-3 py-2 rounded-lg">
            ✏️ Edit in builder
          </button>
        </div>
      )}

      {plan && (
        <>
          {tripIsActive && todaySchedule && (
            <div className="bg-gradient-to-br from-rose-500/10 to-amber-500/5 border-2 border-rose-500/40 rounded-2xl p-4 mb-4">
              <div className="text-[10px] font-bold text-rose-300 uppercase tracking-wider mb-1">📍 TODAY · Day {currentDay} · {todaySchedule.city}</div>
              <div className="text-base font-black text-brand-ink mb-2">{todaySchedule.theme}</div>
              <div className="space-y-2">
                {todaySchedule.blocks.map((b, i) => <DashboardBlock key={i} block={b} city={todaySchedule.city} />)}
              </div>
            </div>
          )}

          {plan.intro_summary && (
            <Card title="The plan" icon="📋">
              <p className="text-sm text-brand-ink leading-relaxed">{plan.intro_summary}</p>
            </Card>
          )}

          {plan.budget_tally && (
            <Card title="Budget" icon="💰">
              <div className="grid grid-cols-3 gap-2">
                <Tile label="Amex MR" value={(plan.budget_tally.amex_mr_used || 0).toLocaleString()} />
                <Tile label="Chase UR" value={(plan.budget_tally.chase_ur_used || 0).toLocaleString()} />
                <Tile label="Cash" value={`$${(plan.budget_tally.cash_used || 0).toLocaleString()}`} />
              </div>
              <div className={`mt-2 text-xs font-bold ${plan.budget_tally.fits_constraints ? 'text-teal' : 'text-rose-300'}`}>
                {plan.budget_tally.fits_constraints ? '✓ Within your caps' : '⚠ Exceeds caps — chat with Pilot to trim'}
              </div>
            </Card>
          )}

          {stops.length > 0 && (
            <Card title="Stops" icon="📍">
              <div className="space-y-2">
                {stops.map((s, i) => {
                  const stopHotel = hotels.find(h => h.city === s.city);
                  return (
                    <div key={i} className="bg-navy-light border border-brand-ink/15 rounded-lg p-2.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-bold text-brand-ink">{s.city}</span>
                        <span className="text-sm font-bold text-teal">{s.nights}n</span>
                      </div>
                      <p className="text-[11px] text-brand-ink/75 leading-snug mt-1">{s.reasoning}</p>
                      {stopHotel?.hotel && (
                        <div className="mt-2 pt-2 border-t border-brand-ink/10">
                          <div className="text-[10px] font-bold text-gold uppercase">Hotel</div>
                          <div className="text-xs font-bold text-brand-ink">{stopHotel.hotel.hotel_name}</div>
                          <div className="text-[10px] text-brand-ink/60">{stopHotel.hotel.hotel_program} · {stopHotel.hotel.neighborhood} · {(stopHotel.hotel.total_points || 0).toLocaleString()} pts</div>
                          <LinkRow links={getHotelLinks(stopHotel.hotel, s.city)} />
                          {stopHotel.hotel.phone && (
                            <a href={`tel:${stopHotel.hotel.phone}`} className="inline-block mt-1 text-[10px] text-teal font-bold">📞 {stopHotel.hotel.phone}</a>
                          )}
                          <DashboardBookingGuide steps={getHotelBookingSteps(stopHotel.hotel)} kind="hotel" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {flights.length > 0 && (
            <Card title="Flights" icon="✈️">
              <div className="space-y-2">
                {flights.map((fp, i) => fp.picked && (
                  <div key={i} className="bg-navy-light border border-brand-ink/15 rounded-lg p-2.5">
                    <div className="text-[10px] font-bold text-teal uppercase tracking-wider">Leg {fp.leg_index} · {fp.leg_label}</div>
                    <div className="text-xs font-bold text-brand-ink mt-0.5">{fp.picked.source} · {fp.picked.airlines || 'multi'}</div>
                    <div className="text-[10px] text-brand-ink/65 flex flex-wrap gap-x-2 mt-0.5">
                      <span>{fp.picked.date}</span>
                      <span>· {fp.picked.miles_per_person.toLocaleString()} mi/pp</span>
                      <span>· ${fp.picked.taxes_total} tax</span>
                      <span>· {fp.picked.direct ? <strong className="text-teal">DIRECT</strong> : 'connecting'}</span>
                    </div>
                    <LinkRow links={getFlightLinks(fp.picked)} />
                    <DashboardBookingGuide steps={getFlightBookingSteps(fp.picked, fp.picked.funded_by?.[0])} kind="flight" />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {schedule.length > 0 && (
            <Card title="Day-by-day" icon="📅">
              <div className="space-y-2">
                {schedule.map((d) => (
                  <details key={d.day_number} className="bg-navy-light border border-brand-ink/15 rounded-lg" open={d.day_number === currentDay}>
                    <summary className="p-2.5 cursor-pointer list-none flex items-baseline justify-between gap-2">
                      <div>
                        <div className="text-[10px] font-bold text-gold uppercase">Day {d.day_number} · {d.city}</div>
                        <div className="text-xs font-bold text-brand-ink">{d.theme}</div>
                      </div>
                      <span className="text-[10px] text-brand-ink/55">{d.blocks.length} blocks</span>
                    </summary>
                    <div className="p-2.5 pt-0 space-y-2 border-t border-brand-ink/10">
                      {d.blocks.map((b, i) => <DashboardBlock key={i} block={b} city={d.city} />)}
                    </div>
                  </details>
                ))}
              </div>
            </Card>
          )}

          {activities.length > 0 && (
            <Card title="Activities" icon="🎯">
              <div className="space-y-1.5">
                {activities.map((a, i) => (
                  <div key={i} className="bg-navy-light border border-brand-ink/15 rounded-lg p-2">
                    <div className="text-xs font-bold text-brand-ink">{a.activity.name}</div>
                    <div className="text-[10px] text-brand-ink/60">{a.city || a.activity.category}</div>
                    <LinkRow links={getActivityLinks(a.activity, a.city)} />
                    {a.activity.phone && <a href={`tel:${a.activity.phone}`} className="inline-block mt-1 text-[10px] text-teal font-bold">📞 {a.activity.phone}</a>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {restaurants.length > 0 && (
            <Card title="Restaurants" icon="🍽️">
              <div className="space-y-1.5">
                {restaurants.map((r, i) => (
                  <div key={i} className="bg-navy-light border border-brand-ink/15 rounded-lg p-2">
                    <div className="text-xs font-bold text-brand-ink">{r.restaurant.name}</div>
                    <div className="text-[10px] text-brand-ink/60">{r.city || r.restaurant.cuisine} {r.meal ? `· ${r.meal}` : ''}</div>
                    <LinkRow links={getRestaurantLinks(r.restaurant, r.city)} />
                    {r.restaurant.phone && <a href={`tel:${r.restaurant.phone}`} className="inline-block mt-1 text-[10px] text-teal font-bold">📞 {r.restaurant.phone}</a>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {plan.watchouts && plan.watchouts.length > 0 && (
            <Card title="Watch-outs" icon="⚠️">
              <ul className="space-y-1 list-disc list-inside text-xs text-brand-ink/85">
                {plan.watchouts.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </Card>
          )}

          <button onClick={onOpenPilot} className="w-full mt-4 bg-gradient-to-r from-teal/15 to-gold/15 border border-teal/40 rounded-xl p-3 text-left text-brand-ink hover:from-teal/25 hover:to-gold/25 transition">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal to-gold flex items-center justify-center text-navy font-black">P</div>
              <div>
                <div className="font-bold text-sm">Chat with Pilot</div>
                <div className="text-[11px] text-brand-ink/65">Refine the plan · ask questions · swap picks</div>
              </div>
              <div className="ml-auto text-teal text-xl">→</div>
            </div>
          </button>
        </>
      )}

      {/* Aggregate trip stats — points spent, money saved, cities visited */}
      {(memories.length > 0 || allVersions.length > 0) && (
        <TripStatsSection memories={memories} versions={allVersions} />
      )}

      {/* Past trips + saved versions — browse everything you've ever planned */}
      {(memories.length > 0 || allVersions.length > 0) && (
        <PastTripsSection memories={memories} versions={allVersions} />
      )}
    </div>
  );
}

// TripStatsSection — aggregate analytics across every trip the user has
// ever planned. Pulls from trip_memory (each Apply this plan) + counts
// destinations + sums points + sums savings.
function TripStatsSection({ memories, versions }) {
  const allPlans = [...memories.map(m => m.plan_summary), ...versions.map(v => v.plan)];
  const totalTrips = memories.length;
  const uniqueDestinations = new Set([...memories, ...versions].map(x => x.destination_name).filter(Boolean)).size;
  let totalPoints = 0;
  let totalSavings = 0;
  let cppCount = 0;
  let cppSum = 0;
  allPlans.forEach(p => {
    if (!p) return;
    if (p.budget_tally) {
      totalPoints += (p.budget_tally.amex_mr_used || 0) + (p.budget_tally.chase_ur_used || 0);
    }
    (p.flight_picks || []).forEach(fp => {
      if (fp.picked) {
        totalSavings += (fp.picked.cash_equivalent || 0) - (fp.picked.taxes_total || 0);
        if (fp.picked.cpp) { cppSum += fp.picked.cpp; cppCount++; }
      }
    });
  });
  const avgCpp = cppCount > 0 ? (cppSum / cppCount).toFixed(1) : '—';

  return (
    <div className="bg-gradient-to-br from-emerald-500/10 to-teal/5 border-2 border-emerald-500/30 rounded-2xl p-3.5 mt-3">
      <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider mb-2">📊 Your Travel Stats</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Trips planned" value={totalTrips} accent="text-brand-ink" />
        <StatTile label="Destinations" value={uniqueDestinations} accent="text-teal" />
        <StatTile label="Points used" value={totalPoints.toLocaleString()} accent="text-gold" />
        <StatTile label="Saved cash" value={`$${totalSavings.toLocaleString()}`} accent="text-emerald-300" />
        <StatTile label="Avg CPP" value={avgCpp} accent="text-emerald-300" />
        <StatTile label="Saved versions" value={versions.length} accent="text-brand-ink" />
      </div>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className="bg-navy-light rounded-md p-2 text-center">
      <div className="text-[9px] uppercase font-bold text-brand-ink/55">{label}</div>
      <div className={`text-base font-black mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

// PastTripsSection — single browsable view of every trip the user has
// ever planned (trip_memory entries from each "Apply this plan") plus
// every saved version (trip_versions). Tappable destination chips
// expand to show that destination's saved versions.
function PastTripsSection({ memories, versions }) {
  const [expanded, setExpanded] = useState(null);

  // Group versions by destination
  const versionsByDest = {};
  versions.forEach(v => {
    if (!versionsByDest[v.destination_id]) versionsByDest[v.destination_id] = { name: v.destination_name, versions: [] };
    versionsByDest[v.destination_id].versions.push(v);
  });

  // Merge memories + version-only destinations
  const destinations = new Map();
  memories.forEach(m => {
    if (!destinations.has(m.destination_id)) destinations.set(m.destination_id, { id: m.destination_id, name: m.destination_name, memories: [], versions: [] });
    destinations.get(m.destination_id).memories.push(m);
  });
  Object.entries(versionsByDest).forEach(([id, data]) => {
    const intId = Number(id);
    if (!destinations.has(intId)) destinations.set(intId, { id: intId, name: data.name, memories: [], versions: data.versions });
    else destinations.get(intId).versions = data.versions;
  });

  const destList = Array.from(destinations.values());
  if (destList.length === 0) return null;

  return (
    <div className="bg-navy-light border border-brand-ink/15 rounded-2xl p-3.5 mt-3">
      <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-2">📚 Past Trips & Saved Versions</div>
      <div className="text-[11px] text-brand-ink/60 mb-2">Every trip you've planned + every version you saved. Tap to expand.</div>
      <div className="space-y-1.5">
        {destList.map(d => {
          const isOpen = expanded === d.id;
          const lastPlanned = d.memories[0]?.created_at;
          return (
            <div key={d.id} className="bg-brand-ink/5 rounded-lg border border-brand-ink/10">
              <button
                onClick={() => setExpanded(isOpen ? null : d.id)}
                className="w-full text-left p-2.5 flex items-center justify-between gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-brand-ink truncate">{d.name}</div>
                  <div className="text-[10px] text-brand-ink/60">
                    {d.memories.length > 0 && `${d.memories.length} plan${d.memories.length === 1 ? '' : 's'} applied`}
                    {d.memories.length > 0 && d.versions.length > 0 && ' · '}
                    {d.versions.length > 0 && `${d.versions.length} version${d.versions.length === 1 ? '' : 's'} saved`}
                    {lastPlanned && ` · last ${new Date(lastPlanned).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  </div>
                </div>
                <span className="text-brand-ink/55 text-xs">{isOpen ? '▼' : '▶'}</span>
              </button>
              {isOpen && (
                <div className="px-2.5 pb-2.5 space-y-1.5 border-t border-brand-ink/10 pt-2">
                  {d.versions.length > 0 && (
                    <div>
                      <div className="text-[10px] font-bold text-teal uppercase mb-1">Saved versions</div>
                      {d.versions.map(v => (
                        <div key={v.id} className="bg-navy-light rounded p-1.5 mb-1 text-[10px]">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-brand-ink">{v.version_name}</div>
                              <div className="text-brand-ink/65">
                                {v.vibe?.tripType ? (Array.isArray(v.vibe.tripType) ? v.vibe.tripType.join(', ') : v.vibe.tripType) : 'no type'}
                                {' · '}{new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {v.plan?.budget_tally && ` · ${((v.plan.budget_tally.amex_mr_used || 0) + (v.plan.budget_tally.chase_ur_used || 0)).toLocaleString()} pts + $${(v.plan.budget_tally.cash_used || 0).toLocaleString()} cash`}
                              </div>
                              {v.plan?.intro_summary && (
                                <div className="text-brand-ink/75 mt-1 italic line-clamp-2">{v.plan.intro_summary}</div>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                // Load this version's plan as the active plan
                                // via the API endpoint that backs TripDashboard.
                                import('../api').then(({ api }) => {
                                  api.savePilotChatHistory({ destinationId: d.id, history: [], plan: v.plan }).then(() => {
                                    window.location.reload();
                                  }).catch(() => {});
                                });
                              }}
                              className="text-[10px] font-bold bg-teal text-navy px-2 py-1 rounded shrink-0"
                            >
                              ↑ Load
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {d.memories.length > 0 && (
                    <div>
                      <div className="text-[10px] font-bold text-gold uppercase mb-1">Applied plans</div>
                      {d.memories.map(m => (
                        <div key={m.id} className="bg-navy-light rounded p-1.5 mb-1 text-[10px]">
                          <div className="text-brand-ink/65">
                            {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {m.vibe?.tripType && ` · ${Array.isArray(m.vibe.tripType) ? m.vibe.tripType.join(', ') : m.vibe.tripType}`}
                          </div>
                          {m.plan_summary?.intro && (
                            <div className="text-brand-ink/75 mt-1 italic line-clamp-2">{m.plan_summary.intro}</div>
                          )}
                          {m.plan_summary?.hotels && m.plan_summary.hotels.length > 0 && (
                            <div className="text-brand-ink/60 mt-1">Hotels: {m.plan_summary.hotels.map(h => h.name).filter(Boolean).join(', ')}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Card({ title, icon, children }) {
  return (
    <div className="bg-navy-light border border-brand-ink/15 rounded-2xl p-3.5 mb-3">
      <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-2">{icon} {title}</div>
      {children}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-brand-ink/5 rounded-md p-2">
      <div className="text-[9px] font-bold text-brand-ink/55 uppercase">{label}</div>
      <div className="font-bold text-brand-ink mt-0.5">{value}</div>
    </div>
  );
}

// DashboardBookingGuide — collapsible step-by-step transfer + redeem
// instructions per pick. Auto-expands during print (CSS @media print)
// so the printed/PDF version shows everything.
function DashboardBookingGuide({ steps, kind }) {
  if (!steps || steps.length === 0) return null;
  return (
    <details className="mt-2 bg-emerald-500/5 border border-emerald-500/25 rounded p-2 group print-open">
      <summary className="cursor-pointer text-[10px] font-bold text-emerald-300 list-none flex items-center gap-1">
        <span className="group-open:rotate-90 inline-block transition">▶</span>
        <span>📋 How to redeem & book — {steps.length} steps</span>
      </summary>
      <ol className="mt-2 space-y-1.5">
        {steps.map((s, i) => (
          <li key={i} className="text-[11px] text-brand-ink/85 leading-snug">
            <div className="flex gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 font-black text-[10px] shrink-0">{s.step}</span>
              <div className="flex-1">
                <div className="font-bold">{s.title}</div>
                <div className="text-brand-ink/75 mt-0.5">{s.detail}</div>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-0.5 text-[10px] text-teal font-bold">Open {kind === 'flight' ? 'airline' : 'hotel'} site ↗</a>
                )}
                {s.warning && (
                  <div className="text-[10px] text-amber-300 mt-1 bg-amber-500/5 border border-amber-500/20 rounded px-1.5 py-1">{s.warning}</div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}

function DashboardBlock({ block, city }) {
  const kindEmoji = { breakfast: '☕', morning: '☀️', lunch: '🥗', afternoon: '🌤️', dinner: '🍽️', evening: '🌙', travel: '🚆', rest: '😴' };
  return (
    <div className="flex gap-2">
      <div className="shrink-0 w-12 text-right pt-0.5">
        <div className="text-[10px] font-bold text-teal">{block.time}</div>
        <div className="text-base">{kindEmoji[block.kind] || '•'}</div>
      </div>
      <div className="flex-1 bg-brand-ink/5 rounded-md p-2">
        {block.activity && <div className="text-xs font-bold text-brand-ink">{block.activity.name}</div>}
        {block.restaurant && <div className="text-xs font-bold text-brand-ink">{block.restaurant.name}</div>}
        <p className="text-[11px] text-brand-ink/80 leading-snug mt-0.5">{block.description}</p>
        {block.activity && <LinkRow links={getActivityLinks(block.activity, city)} />}
        {block.restaurant && <LinkRow links={getRestaurantLinks(block.restaurant, city)} />}
      </div>
    </div>
  );
}
